import React, { useEffect, useState } from 'react';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { Check, Mail, Store, Palette, Info as InfoIcon, Languages, Clock, Star, ShieldCheck, CreditCard, ExternalLink, CreditCard as CardIcon, AlertCircle, FileText, Volume2 } from 'lucide-react';
import { TwoFactorSettings } from '../components/TwoFactorSettings';
import { OpeningHoursSettings } from '../components/OpeningHoursSettings';
import { RestaurantBrandingSettings } from '../components/RestaurantBrandingSettings';
import { RestaurantClientInfoSettings } from '../components/RestaurantClientInfoSettings';
import { MenuLanguageSettings } from '../components/MenuLanguageSettings';
import { RestaurantTestimonialSettings } from '../components/RestaurantTestimonialSettings';
import { BillingProfileEditor } from '../components/BillingProfileEditor';
import { NotificationSoundsSettings } from '../components/NotificationSoundsSettings';
import { PLANS, planById } from '../lib/plans';
import { BillingCyclePicker } from '../components/BillingCyclePicker';
import { PlanPriceDisplay } from '../components/PlanPriceDisplay';

const SETTINGS_TABS = [
    { id: 'profil', label: 'Profil', icon: Store },
    { id: 'branding', label: 'Branding', icon: Palette },
    { id: 'client', label: 'Info client', icon: InfoIcon },
    { id: 'languages', label: 'Limbi meniu', icon: Languages },
    { id: 'hours', label: 'Program', icon: Clock },
    { id: 'testimonials', label: 'Recenzii', icon: Star },
    { id: 'sounds', label: 'Sunete', icon: Volume2 },
    { id: 'security', label: 'Securitate', icon: ShieldCheck },
    { id: 'billing-profile', label: 'Date facturare', icon: FileText },
    { id: 'billing', label: 'Plan & facturare', icon: CreditCard },
];

export default function SettingsPage() {
    const { restaurant, user } = useAuth();
    const currentPlan = planById(restaurant?.plan);
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [tab, setTab] = useState('profil');
    const [paywallMsg, setPaywallMsg] = useState('');

    useEffect(() => {
        // Triggered by the axios 402 interceptor — backend says this location has
        // no active subscription. Land on the Plan tab with the message visible.
        const params = new URLSearchParams(window.location.search);
        if (params.get('paywall') === '1') {
            const msg = sessionStorage.getItem('tf_paywall_msg') || 'Abonament expirat sau inactiv pentru această locație.';
            setPaywallMsg(msg);
            setTab('billing');
            sessionStorage.removeItem('tf_paywall_msg');
        }
    }, []);

    return (
        <div className="fade-in-up" data-testid="settings-page">
            <div className="kicker">Setări</div>
            <h1 className="headline" style={{ fontSize: '2.4rem', margin: '0.3rem 0 1.4rem' }}>Profilul restaurantului tău.</h1>

            {paywallMsg && (
                <div className="panel" style={{
                    marginBottom: '1.2rem', padding: '0.85rem 1.1rem',
                    background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.45)',
                    display: 'flex', alignItems: 'center', gap: '0.6rem',
                }} data-testid="paywall-banner">
                    <AlertCircle size={18} style={{ color: 'var(--amber)', flexShrink: 0 }} />
                    <span style={{ fontSize: '0.92rem' }}>{paywallMsg}</span>
                </div>
            )}

            <div className="settings-subtabs" role="tablist" aria-label="Secțiuni setări">
                {SETTINGS_TABS.map(({ id, label, icon: Icon }) => (
                    <button
                        key={id}
                        type="button"
                        role="tab"
                        aria-selected={tab === id}
                        className={`admin-tabs__btn${tab === id ? ' admin-tabs__btn--active' : ''}`}
                        onClick={() => setTab(id)}
                        data-testid={`settings-tab-${id}`}
                    >
                        <Icon size={15} />
                        {label}
                    </button>
                ))}
            </div>

            <div className="admin-tab-content">
                {tab === 'profil' && (
                    <div data-testid="settings-tab-panel-profil">
                        <div className="panel" style={{ maxWidth: 640 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <Info label="Nume" value={restaurant?.name} />
                                <Info label="Slug" value={restaurant?.slug} />
                                <Info label="Plan" value={<span className="badge badge-amber">{currentPlan.name}</span>} />
                                <Info label="Status" value={<span className={`badge badge-${restaurant?.status === 'active' ? 'teal' : 'cyan'}`}>{restaurant?.status}</span>} />
                                <Info label="Telefon" value={restaurant?.phone || '—'} />
                                <Info label="Monedă" value={restaurant?.currency} />
                                <Info label="Cont admin" value={user?.email} />
                                <Info label="Creat la" value={restaurant?.created_at?.slice(0, 10)} />
                                <Info label="Cod login echipă" value={restaurant?.staff_login_id || '—'} />
                            </div>
                            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '0.85rem', marginBottom: 0, lineHeight: 1.5 }}>
                                Distribuie codul restaurantului împreună cu ID-ul și PIN-ul fiecărui ospătar/bucătar pentru login rapid.
                            </p>
                        </div>
                    </div>
                )}

                {tab === 'branding' && (
                    <div data-testid="settings-tab-panel-branding">
                        <RestaurantBrandingSettings />
                    </div>
                )}

                {tab === 'client' && (
                    <div data-testid="settings-tab-panel-client">
                        <RestaurantClientInfoSettings />
                    </div>
                )}

                {tab === 'languages' && (
                    <div data-testid="settings-tab-panel-languages">
                        <MenuLanguageSettings />
                    </div>
                )}

                {tab === 'hours' && (
                    <div data-testid="settings-tab-panel-hours">
                        <OpeningHoursSettings />
                    </div>
                )}

                {tab === 'testimonials' && (
                    <div data-testid="settings-tab-panel-testimonials">
                        <RestaurantTestimonialSettings />
                    </div>
                )}

                {tab === 'sounds' && (
                    <div data-testid="settings-tab-panel-sounds">
                        <NotificationSoundsSettings />
                    </div>
                )}

                {tab === 'security' && (
                    <div data-testid="settings-tab-panel-security">
                        <TwoFactorSettings />
                    </div>
                )}

                {tab === 'billing-profile' && (
                    <div data-testid="settings-tab-panel-billing-profile">
                        <BillingProfileEditor />
                    </div>
                )}

                {tab === 'billing' && <BillingTab restaurant={restaurant} billingCycle={billingCycle} setBillingCycle={setBillingCycle} setTab={setTab} />}
            </div>
        </div>
    );
}

const Info = ({ label, value }) => (
    <div>
        <div className="kicker" style={{ marginBottom: 4 }}>{label}</div>
        <div style={{ color: '#fff', fontWeight: 500 }}>{value}</div>
    </div>
);


function daysUntil(iso) {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}


// Toggle for the temporary "payments disabled" mode while Revolut Live
// are being approved. Set REACT_APP_PAYMENTS_ENABLED=true in .env.production when ready.
const PAYMENTS_ENABLED = String(process.env.REACT_APP_PAYMENTS_ENABLED || 'false').toLowerCase() === 'true';
const SUPPORT_EMAIL = 'contact@clickmeniu.com';

function BillingTab({ restaurant, billingCycle, setBillingCycle, setTab }) {
    const [chosenPlan, setChosenPlan] = useState(restaurant?.plan || 'pro');
    // Tracks which processor button is mid-checkout so its spinner shows while
    // the other one stays disabled but not spinning. Empty string = idle.
    const [pendingProcessor, setPendingProcessor] = useState('');
    const [billingProfile, setBillingProfile] = useState(null);
    // Distinct from `loading` (checkout button). Until the /r/billing fetch
    // resolves we don't actually know whether billing is complete — without
    // this gate, the warning panel flashed red for ~200ms every time the user
    // clicked into this tab.
    const [profileLoaded, setProfileLoaded] = useState(false);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [recheckingId, setRecheckingId] = useState('');
    const [resendingId, setResendingId] = useState('');

    const resendInvoiceEmail = async (paymentId) => {
        setResendingId(paymentId);
        try {
            const { data } = await api.post(`/r/subscription/payments/${paymentId}/resend-email`);
            alert(`Email-ul cu factura a fost trimis la ${data.sent_to}.`);
        } catch (e) {
            alert(formatApiError(e));
        } finally {
            setResendingId('');
        }
    };

    // Re-initiate checkout for a failed/cancelled payment using its own plan + cycle,
    // so a customer whose card was declined can pay again in one click.
    const retryPayment = async (p) => {
        if (pendingProcessor) return;
        setPendingProcessor(p.processor);
        try {
            const { data } = await api.post('/r/subscription/checkout', {
                plan: p.plan, billing_cycle: p.billing_cycle, processor: p.processor,
            });
            if (data.checkout_url) {
                window.location.href = data.checkout_url;
            } else {
                alert('Nu am primit URL de plată.');
                setPendingProcessor('');
            }
        } catch (e) {
            alert(formatApiError(e));
            setPendingProcessor('');
        }
    };

    const recheckPayment = async (paymentId) => {
        setRecheckingId(paymentId);
        try {
            const { data } = await api.post(`/r/subscription/payments/${paymentId}/recheck`);
            const fresh = await api.get('/r/subscription/payments').then(r => r.data.items || []);
            setPayments(fresh);
            if (data.status === 'succeeded') {
                alert('Plata a fost confirmată! Abonamentul tău e acum activ.');
                window.location.reload();
            } else {
                alert(data.note || 'Procesatorul încă nu a confirmat plata. Încearcă din nou peste 1 minut.');
            }
        } catch (e) {
            alert(formatApiError(e));
        } finally {
            setRecheckingId('');
        }
    };

    useEffect(() => {
        Promise.all([
            api.get('/r/billing').then(r => r.data).catch(() => null),
            api.get('/r/subscription/payments').then(r => r.data.items || []).catch(() => []),
        ]).then(([b, p]) => {
            setBillingProfile(b);
            setPayments(p);
            setProfileLoaded(true);
        });
    }, []);

    const daysLeft = daysUntil(restaurant?.subscription_expires_at);
    const isTrial = restaurant?.status === 'trial';
    const isActive = restaurant?.status === 'active';
    const billingComplete = billingProfile?.billing?.cui && billingProfile?.billing?.legal_name;
    // Only render the "incomplete billing" warning once we've actually heard
    // back from the server — the initial null is "unknown", not "missing".
    const showIncompleteBilling = profileLoaded && !billingComplete;
    // Renewal window: backend allows checkout only when active sub has ≤ 15 days left.
    // Trial / suspended / expired = always allowed.
    const RENEWAL_WINDOW = 15;
    const tooEarlyToRenew = isActive && daysLeft !== null && daysLeft > RENEWAL_WINDOW;

    const startCheckout = async (processor) => {
        if (pendingProcessor) return;
        setError(''); setPendingProcessor(processor); setLoading(true);
        try {
            const { data } = await api.post('/r/subscription/checkout', {
                plan: chosenPlan, billing_cycle: billingCycle, processor,
            });
            if (data.checkout_url) {
                window.location.href = data.checkout_url;
            } else {
                setError('Nu am primit URL de plată.');
                setPendingProcessor('');
            }
        } catch (e) {
            setError(formatApiError(e));
            setPendingProcessor('');
        } finally { setLoading(false); }
    };

    const MAX_W = 900;
    const PAYMENT_STATUS_LABEL = {
        succeeded: 'Confirmată', pending: 'În așteptare', failed: 'Respinsă',
        cancelled: 'Anulată', refunded: 'Returnată', amount_mismatch: 'Sumă greșită',
    };
    const isFailedPayment = (s) => s === 'failed' || s === 'cancelled';
    return (
        <div data-testid="settings-tab-panel-billing" style={{ maxWidth: MAX_W }}>
            {/* Header — responsive font + clean status line */}
            <div style={{ marginBottom: '1.4rem' }}>
                <div className="kicker">Plan & facturare</div>
                <h2 className="headline page-title" style={{ margin: '0.3rem 0 0.5rem' }}>
                    {isTrial ? 'Activează abonamentul' : 'Abonamentul tău'}
                </h2>
                {daysLeft !== null && (
                    <p style={{ color: daysLeft <= 5 ? 'var(--neon-magenta)' : 'var(--text-muted)', margin: 0, fontSize: '0.92rem' }}>
                        {isTrial
                            ? (daysLeft > 0 ? `Trial expiră în ${daysLeft} zile.` : 'Trial-ul a expirat.')
                            : (daysLeft > 0 ? `Activ până la ${new Date(restaurant.subscription_expires_at).toLocaleDateString('ro-RO')} (${daysLeft} zile rămase).` : 'Abonamentul a expirat.')}
                    </p>
                )}
            </div>

            {showIncompleteBilling && (
                <div className="panel" style={{ marginBottom: '1.2rem', borderColor: 'var(--neon-magenta)', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <AlertCircle size={18} color="var(--neon-magenta)" style={{ marginTop: 2, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                        <strong>Date facturare incomplete</strong>
                        <p style={{ margin: '4px 0 10px', fontSize: '0.88rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                            Înainte de a plăti, completează datele firmei (CUI, denumire, adresă) — vor apărea automat pe factură.
                        </p>
                        <button type="button" className="btn btn-primary btn-sm" onClick={() => setTab?.('billing-profile')} data-testid="goto-billing-profile">
                            <FileText size={14} /> Completează datele de facturare
                        </button>
                    </div>
                </div>
            )}

            {/* Section: cycle picker (1 lună / 3 / 6 / anual) */}
            <div style={{ marginBottom: '1.2rem' }}>
                <div className="kicker" style={{ marginBottom: 8 }}>Perioadă facturare</div>
                <BillingCyclePicker value={billingCycle} onChange={setBillingCycle} testId="settings-billing-cycle" />
            </div>

            {/* Section: plan picker — cards aliniate, înălțime egală via flex column */}
            <div style={{ marginBottom: '1.4rem' }}>
                <div className="kicker" style={{ marginBottom: 8 }}>Alege planul</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))', gap: '0.9rem' }}>
                    {PLANS.map(p => {
                        const isCurrent = restaurant?.plan === p.id;
                        const isChosen = chosenPlan === p.id;
                        return (
                            <div key={p.id} className="panel"
                                 style={{
                                     border: isChosen ? '2px solid var(--amber)' : (isCurrent ? '1px solid var(--brand-teal-light)' : '1px solid var(--border)'),
                                     cursor: 'pointer',
                                     display: 'flex',
                                     flexDirection: 'column',
                                     minHeight: 220,
                                     transition: 'border-color 0.15s',
                                 }}
                                 onClick={() => setChosenPlan(p.id)}
                                 data-testid={`plan-card-${p.id}`}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                    <div className="kicker">{p.name}</div>
                                    {isCurrent && <span className="badge badge-cyan">curent</span>}
                                </div>
                                <PlanPriceDisplay plan={p} cycleId={billingCycle} size="md" variant="card" />
                                <ul style={{ listStyle: 'none', padding: 0, margin: '0.8rem 0 0', display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                                    {p.perks.slice(0, 4).map((perk, i) => (
                                        <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, color: 'var(--text-muted)', fontSize: '0.86rem', lineHeight: 1.4 }}>
                                            <Check size={12} color="var(--neon-teal)" style={{ marginTop: 4, flexShrink: 0 }} /> <span>{perk}</span>
                                        </li>
                                    ))}
                                </ul>
                                {isChosen && (
                                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)', fontSize: '0.78rem', color: 'var(--amber)', textAlign: 'center', fontWeight: 600 }}>
                                        ✓ Plan ales
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {PAYMENTS_ENABLED ? (
                <>
                    {tooEarlyToRenew && (
                        <div className="panel" style={{ marginBottom: '1rem', borderColor: 'var(--brand-teal-light)', display: 'flex', gap: 12, alignItems: 'flex-start' }}
                             data-testid="too-early-renewal">
                            <Check size={20} color="var(--neon-teal)" style={{ marginTop: 2, flexShrink: 0 }} />
                            <div>
                                <strong style={{ display: 'block', marginBottom: 6 }}>Abonamentul tău e activ — nimic de făcut acum</strong>
                                <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.5 }}>
                                    Mai ai <strong style={{ color: 'var(--neon-teal)' }}>{daysLeft} zile</strong> din abonamentul curent.
                                    Reabonarea devine disponibilă cu <strong>{RENEWAL_WINDOW} zile</strong> înainte de expirare —
                                    așa eviți să plătești pentru o perioadă pe care nu o vei consuma.
                                </p>
                                <p style={{ margin: '6px 0 0', color: 'var(--text-faint)', fontSize: '0.82rem' }}>
                                    Vei primi un email de reamintire automat cu câteva zile înainte de expirare.
                                </p>
                            </div>
                        </div>
                    )}
                    <div className="panel" style={{ marginBottom: '1rem', opacity: tooEarlyToRenew ? 0.5 : 1 }}>
                        <div className="kicker" style={{ marginBottom: 10 }}>Metodă plată — click pentru a continua</div>

                        {error && <div style={{ color: 'var(--neon-magenta)', marginBottom: 10, fontSize: '0.88rem' }} data-testid="checkout-error">{error}</div>}

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                            <button
                                type="button"
                                className="btn btn-primary"
                                style={{ justifyContent: 'flex-start', padding: '0.9rem 1rem', gap: 10 }}
                                disabled={loading || !billingComplete || tooEarlyToRenew}
                                title={
                                    tooEarlyToRenew ? `Reabonarea e disponibilă cu ${RENEWAL_WINDOW} zile înainte de expirare` :
                                    !billingComplete ? 'Completează datele de facturare mai sus' : 'Plătește cu Revolut Pay'
                                }
                                onClick={() => startCheckout('revolut')}
                                data-testid="proc-revolut"
                            >
                                {pendingProcessor === 'revolut'
                                    ? <span className="spinner" />
                                    : <CardIcon size={16} />}
                                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.2 }}>
                                    <strong>Revolut Pay</strong>
                                    <span style={{ fontSize: '0.72rem', opacity: 0.85, fontWeight: 400 }}>Card, Apple Pay, Google Pay</span>
                                </span>
                                <ExternalLink size={14} style={{ marginLeft: 'auto', opacity: 0.7 }} />
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                style={{ justifyContent: 'flex-start', padding: '0.9rem 1rem', gap: 10 }}
                                disabled={loading || !billingComplete || tooEarlyToRenew}
                                title={
                                    tooEarlyToRenew ? `Reabonarea e disponibilă cu ${RENEWAL_WINDOW} zile înainte de expirare` :
                                    !billingComplete ? 'Completează datele de facturare mai sus' : 'Plătește cu cardul prin Netopia'
                                }
                                onClick={() => startCheckout('netopia')}
                                data-testid="proc-netopia"
                            >
                                {pendingProcessor === 'netopia'
                                    ? <span className="spinner" />
                                    : <CardIcon size={16} />}
                                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.2 }}>
                                    <strong>Card bancar (Netopia)</strong>
                                    <span style={{ fontSize: '0.72rem', opacity: 0.85, fontWeight: 400 }}>Visa, Mastercard</span>
                                </span>
                                <ExternalLink size={14} style={{ marginLeft: 'auto', opacity: 0.7 }} />
                            </button>
                        </div>

                        <p style={{ marginTop: 12, marginBottom: 0, color: 'var(--text-muted)', fontSize: '0.8rem', lineHeight: 1.5 }}>
                            Plată sigură. Factura SmartBill se emite automat și o primești pe email după confirmare.
                        </p>
                    </div>
                </>
            ) : (
                <div className="panel" style={{ maxWidth: 640, display: 'flex', gap: 12, alignItems: 'flex-start', padding: '1rem' }}
                     data-testid="payment-coming-soon">
                    <Mail size={20} color="var(--amber)" style={{ marginTop: 2, flexShrink: 0 }} />
                    <div>
                        <strong style={{ display: 'block', marginBottom: 6 }}>Activarea abonamentului — temporar manuală</strong>
                        <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.5 }}>
                            Plata online prin card va fi disponibilă în curând. Pentru activare,
                            upgrade plan sau orice întrebare scrie-ne la{' '}
                            <a href={`mailto:${SUPPORT_EMAIL}?subject=Activare%20abonament%20-%20${encodeURIComponent(restaurant?.name || '')}`}
                               style={{ color: 'var(--amber)' }}>{SUPPORT_EMAIL}</a>
                            {' '}— răspundem în maxim 24 ore.
                        </p>
                    </div>
                </div>
            )}

            {payments.length > 0 && (
                <div style={{ marginTop: '2rem' }}>
                    <div className="kicker" style={{ marginBottom: 8 }}>Istoric plăți</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {payments.map(p => (
                            <div key={p.id} className="panel" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.7rem 0.9rem', gap: 8 }}>
                                <div>
                                    <div style={{ fontWeight: 600 }}>{p.plan.toUpperCase()} · {p.billing_cycle} · {p.amount_ron.toFixed(2)} RON</div>
                                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                                        {new Date(p.created_at).toLocaleString('ro-RO')} · {p.processor}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    {(p.status === 'pending' || (p.status === 'succeeded' && !p.invoice_id)) && (
                                        <button
                                            type="button"
                                            className="btn btn-sm"
                                            onClick={() => recheckPayment(p.id)}
                                            disabled={recheckingId === p.id}
                                            title={p.status === 'pending'
                                                ? 'Cere procesatorului să confirme plata din nou'
                                                : 'Reemite factura pentru plata confirmată'}
                                        >
                                            {recheckingId === p.id
                                                ? 'Verific...'
                                                : (p.status === 'pending' ? 'Verifică plata' : 'Emite factura')}
                                        </button>
                                    )}
                                    {p.status === 'succeeded' && p.invoice_id && (
                                        <button
                                            type="button"
                                            className="btn btn-sm"
                                            onClick={() => resendInvoiceEmail(p.id)}
                                            disabled={resendingId === p.id}
                                            title="Retrimite email-ul cu factura"
                                        >
                                            {resendingId === p.id ? 'Trimit...' : 'Retrimite email'}
                                        </button>
                                    )}
                                    {isFailedPayment(p.status) && (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-primary"
                                            onClick={() => retryPayment(p)}
                                            disabled={pendingProcessor !== '' || !billingComplete}
                                            title={billingComplete ? 'Reia plata pentru acest abonament' : 'Completează datele de facturare mai sus'}
                                        >
                                            {pendingProcessor !== '' ? 'Se deschide...' : 'Plătește din nou'}
                                        </button>
                                    )}
                                    <span className={`badge ${p.status === 'succeeded' ? 'badge-teal' : p.status === 'pending' ? 'badge-amber' : 'badge-magenta'}`}>
                                        {PAYMENT_STATUS_LABEL[p.status] || p.status}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
